/**
 * CoverageAuditEncounterDetail
 *
 * Superadmin Coverage Audit — single encounter detail view.
 *
 * Layout: 12-col grid → Transcript | Summary | Note/SOAP | Issues rail.
 *  - Transcript: rendered as speaker + timestamp segments (parent
 *    `transcripts.content` is unreliable — see project memory entry on the
 *    transcript double-encryption bug).
 *  - Summary: structured `ClinicalSummary` sections rendered as cards with
 *    status badges and confidence indicators.
 *  - Note/SOAP: toggles between the structured medical-note artifact and a
 *    derived SOAP layout. SOAP isn't persisted today — we synthesize it
 *    from the existing sections (S = subjective sections, O = ROS / exam,
 *    A = assessment notes, P = plan).
 *  - Issues rail: two stacks (issues vs enrichments) capped to top-5.
 */
import { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { apiService } from '../services/api';
import {
  useCoverageAuditStore,
  type IssueRow,
  type IssueSeverity,
  type RunPipelineDebug,
} from '../stores/coverageAuditStore';
import { IssueCard } from '../components/CoverageAudit/IssueCard';
import { useEvidenceScroll } from '../hooks/useEvidenceScroll';
import {
  CLINICAL_SECTION_LABELS,
  CLINICAL_SECTION_ORDER,
  type ClinicalSection,
  type ClinicalSummary,
  type ClinicalSummaryData,
} from '../types/clinicalSummary';
import {
  MEDICAL_NOTE_SECTION_LABELS,
  MEDICAL_NOTE_SECTION_ORDER,
  type MedicalNote,
  type MedicalNoteMetadata,
  type MedicalNoteSection,
} from '../types/medicalNote';

// ----------------------------------------------------------------------------
// Response type
// ----------------------------------------------------------------------------

interface TranscriptSegmentDTO {
  order: number;
  startSeconds: number;
  endSeconds: number;
  speakerRole: string | null;
  speakerName: string | null;
  text: string;
}

interface ClinicalEncounterDetailResponse {
  success: boolean;
  data: {
    clinicalEncounter: {
      id: string;
      mode: 'connected' | 'standalone';
      status: string;
      encounterId: string | null;
      appointmentId: string | null;
      createdAt: string;
    };
    encounter: {
      id: string;
      patientData: { firstName: string | null; lastName: string | null };
      encounterDetails: {
        scheduledDate: string | null;
        type: string | null;
      };
    } | null;
    transcript: {
      content: string;
      segments: TranscriptSegmentDTO[];
      durationSeconds: number;
      wordCount: number;
    } | null;
    summary: ClinicalSummaryData | null;
    medicalNote: {
      found: boolean;
      noteId?: string;
      format?: 'structured' | 'html' | 'markdown' | 'text';
      note?: MedicalNote | string | unknown;
      metadata?: MedicalNoteMetadata;
    };
    warnings: string[];
  };
}

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------

const SEVERITY_ORDER: Record<IssueSeverity, number> = {
  major: 0,
  moderate: 1,
  minor: 2,
};

function sortIssues(issues: IssueRow[]): IssueRow[] {
  return [...issues].sort((a, b) => {
    const sev = SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity];
    if (sev !== 0) return sev;
    return b.confidence - a.confidence;
  });
}

function formatTimestamp(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

// Speaker color tokens. Aligned with the listing page's source badges so
// the audit experience feels consistent across screens.
const SPEAKER_STYLES: Record<
  string,
  { label: string; bg: string; ring: string; dot: string }
> = {
  physician: { label: 'Physician', bg: 'bg-blue-50', ring: 'ring-blue-200', dot: 'bg-blue-500' },
  patient: { label: 'Patient', bg: 'bg-emerald-50', ring: 'ring-emerald-200', dot: 'bg-emerald-500' },
  nurse: { label: 'Nurse', bg: 'bg-purple-50', ring: 'ring-purple-200', dot: 'bg-purple-500' },
  family: { label: 'Family', bg: 'bg-amber-50', ring: 'ring-amber-200', dot: 'bg-amber-500' },
  interpreter: { label: 'Interpreter', bg: 'bg-pink-50', ring: 'ring-pink-200', dot: 'bg-pink-500' },
  other: { label: 'Other', bg: 'bg-gray-50', ring: 'ring-gray-200', dot: 'bg-gray-400' },
};

// SOAP grouping. The existing section names map cleanly onto S/O/A/P with
// only one ambiguous case (treatment), which we surface under both A and P
// because in this codebase it covers procedures performed *and* the post-
// visit plan portion.
type SectionKey = keyof ClinicalSummary;
const SOAP_GROUPS: ReadonlyArray<{
  letter: 'S' | 'O' | 'A' | 'P';
  label: string;
  description: string;
  keys: SectionKey[];
}> = [
  {
    letter: 'S',
    label: 'Subjective',
    description: 'What the patient reports — chief complaint, history, social context.',
    keys: [
      'chiefComplaint',
      'historyOfPresentIllness',
      'pastMedicalHistory',
      'familyHistory',
      'socialHistory',
      'psychiatricHistory',
      'medications',
    ],
  },
  {
    letter: 'O',
    label: 'Objective',
    description: 'Review of systems, physical exam findings, and diagnostics.',
    keys: ['reviewOfSystems'],
  },
  {
    letter: 'A',
    label: 'Assessment',
    description: 'Physician-stated impression and treatment performed during the visit.',
    keys: ['assessment', 'treatment'],
  },
  {
    letter: 'P',
    label: 'Plan',
    description: 'Post-visit plan: prescriptions, follow-up, referrals.',
    keys: ['plan'],
  },
];

const SOAP_LETTER_STYLES: Record<'S' | 'O' | 'A' | 'P', string> = {
  S: 'bg-blue-600',
  O: 'bg-emerald-600',
  A: 'bg-amber-600',
  P: 'bg-purple-600',
};

// Type guards — the API can return a string for non-structured notes.
function isStructuredNote(value: unknown): value is MedicalNote {
  return (
    typeof value === 'object' &&
    value !== null &&
    'chiefComplaint' in (value as Record<string, unknown>)
  );
}

function isStructuredSummary(value: unknown): value is ClinicalSummary {
  return (
    typeof value === 'object' &&
    value !== null &&
    'chiefComplaint' in (value as Record<string, unknown>)
  );
}

// ----------------------------------------------------------------------------
// Page
// ----------------------------------------------------------------------------

export default function CoverageAuditEncounterDetail() {
  const { id: clinicalEncounterId } = useParams<{ id: string }>();

  // Encounter clinical payload.
  const [transcriptSegments, setTranscriptSegments] = useState<
    TranscriptSegmentDTO[]
  >([]);
  const [transcriptDuration, setTranscriptDuration] = useState<number>(0);
  const [transcriptWordCount, setTranscriptWordCount] = useState<number>(0);
  const [patientName, setPatientName] = useState<string>('');
  const [encounterDate, setEncounterDate] = useState<string>('');
  const [encounterType, setEncounterType] = useState<string>('');
  const [encounterMode, setEncounterMode] = useState<
    'connected' | 'standalone' | null
  >(null);
  const [dataLoading, setDataLoading] = useState<boolean>(true);
  const [dataError, setDataError] = useState<string | null>(null);

  // Structured clinical summary.
  const [summary, setSummary] = useState<ClinicalSummary | null>(null);

  // Medical note.
  const [note, setNote] = useState<MedicalNote | null>(null);
  const [noteFormat, setNoteFormat] = useState<string | null>(null);
  const [noteRawString, setNoteRawString] = useState<string>('');

  // Note/SOAP toggle.
  const [noteView, setNoteView] = useState<'sections' | 'soap'>('sections');

  // Issues store.
  const issuesByEncounter = useCoverageAuditStore((s) => s.encounterIssues);
  const issuesLoading = useCoverageAuditStore((s) => s.encounterIssuesLoading);
  const issuesError = useCoverageAuditStore((s) => s.encounterIssuesError);
  const loadEncounterIssues = useCoverageAuditStore(
    (s) => s.loadEncounterIssues,
  );
  const runPipeline = useCoverageAuditStore((s) => s.runPipeline);
  const lastRunDebugMap = useCoverageAuditStore((s) => s.lastRunDebug);
  const lastRunDebug = clinicalEncounterId
    ? lastRunDebugMap[clinicalEncounterId]
    : undefined;
  const [running, setRunning] = useState<boolean>(false);
  const [runError, setRunError] = useState<string | null>(null);
  const [autoRanOnce, setAutoRanOnce] = useState<boolean>(false);

  // Evidence anchoring — see hooks/useEvidenceScroll for the contract.
  const { focusEvidence, focusSection } = useEvidenceScroll();
  const [searchParams] = useSearchParams();

  // Effects.
  useEffect(() => {
    if (!clinicalEncounterId) return;
    let cancelled = false;
    setDataLoading(true);
    setDataError(null);
    (async () => {
      try {
        const resp = await apiService.get<ClinicalEncounterDetailResponse>(
          `/v1/clinical/clinical-encounter/${clinicalEncounterId}`,
        );
        if (cancelled) return;
        const d = resp.data;

        const t = d.transcript;
        setTranscriptSegments(t?.segments ?? []);
        setTranscriptDuration(t?.durationSeconds ?? 0);
        setTranscriptWordCount(t?.wordCount ?? 0);

        const enc = d.encounter;
        if (enc?.patientData) {
          const fn = enc.patientData.firstName ?? '';
          const ln = enc.patientData.lastName ?? '';
          setPatientName(`${fn} ${ln}`.trim() || '—');
        } else {
          setPatientName('—');
        }
        if (enc?.encounterDetails?.scheduledDate) {
          const dt = new Date(enc.encounterDetails.scheduledDate);
          setEncounterDate(
            isNaN(dt.getTime())
              ? enc.encounterDetails.scheduledDate
              : dt.toLocaleString(),
          );
        }
        setEncounterType(enc?.encounterDetails?.type ?? '');
        setEncounterMode(d.clinicalEncounter.mode);

        setSummary(d.summary?.aiGenerated ?? null);

        if (d.medicalNote.found) {
          setNoteFormat(d.medicalNote.format ?? null);
          if (isStructuredNote(d.medicalNote.note)) {
            setNote(d.medicalNote.note);
            setNoteRawString('');
          } else if (typeof d.medicalNote.note === 'string') {
            setNote(null);
            setNoteRawString(d.medicalNote.note);
          } else {
            setNote(null);
            setNoteRawString('');
          }
        } else {
          setNote(null);
          setNoteFormat(null);
          setNoteRawString('');
        }

        if (d.warnings && d.warnings.length > 0 && !d.transcript) {
          setDataError(d.warnings[0]);
        }
      } catch (err) {
        if (cancelled) return;
        const msg =
          err instanceof Error ? err.message : 'Failed to load encounter';
        setDataError(msg);
      } finally {
        if (!cancelled) setDataLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clinicalEncounterId]);

  useEffect(() => {
    if (!clinicalEncounterId) return;
    void loadEncounterIssues(clinicalEncounterId);
  }, [clinicalEncounterId, loadEncounterIssues]);

  // Spot-check on first load. The audit pipeline (TAH + LLM judge + policy
  // engine) is the project's existing detector — we just trigger it
  // automatically when an encounter has never been audited so the auditor
  // doesn't have to remember to click the button. Cached at ~$0.0005/run
  // and persists per-runId, so re-visits are free.
  useEffect(() => {
    if (!clinicalEncounterId) return;
    if (autoRanOnce) return;
    if (issuesLoading) return; // wait until the GET resolved
    if (running) return;
    if (dataLoading) return; // need transcript + summary loaded first
    // Only run if we have something to evaluate.
    if (transcriptSegments.length === 0) return;
    // If the encounter already has a persisted run, leave it alone.
    const existing = issuesByEncounter[clinicalEncounterId];
    if (Array.isArray(existing) && existing.length > 0) {
      setAutoRanOnce(true);
      return;
    }
    setAutoRanOnce(true);
    setRunning(true);
    setRunError(null);
    void (async () => {
      try {
        await runPipeline(clinicalEncounterId);
      } catch (err) {
        setRunError(
          err instanceof Error ? err.message : 'Auto spot-check failed',
        );
      } finally {
        setRunning(false);
      }
    })();
  }, [
    clinicalEncounterId,
    autoRanOnce,
    issuesLoading,
    running,
    dataLoading,
    transcriptSegments.length,
    issuesByEncounter,
    runPipeline,
  ]);

  // Derive issue stacks. Split by audit stage (Phase 3.7) so the rail
  // shows two clearly-separated zones: pipeline issues (our AI's
  // conversion quality) vs recording / discovery issues (what the
  // doctor captured in the visit). Enrichments stay on their own
  // channel — they're suggestions, not gating.
  const { pipelineIssues, recordingIssues, enrichmentRows } = useMemo(() => {
    if (!clinicalEncounterId)
      return {
        pipelineIssues: [] as IssueRow[],
        recordingIssues: [] as IssueRow[],
        enrichmentRows: [] as IssueRow[],
      };
    const all = issuesByEncounter[clinicalEncounterId] ?? [];
    // Hide issues the policy engine routed to `log_only` — those are
    // persisted for analytics + trust-loop training but explicitly
    // marked as "do not surface to clinician". Used by detectors with
    // 'soft' tier and by the auto-suppress mechanism for low-trust
    // detectors. Without this filter the rail leaks shadow signals.
    const surfaced = all.filter((r) => r.disposition !== 'log_only');
    const enrichments = surfaced.filter((r) => r.channel === 'enrichment');
    const issuesOnly = surfaced.filter((r) => r.channel !== 'enrichment');
    const recording = issuesOnly.filter((r) => r.stage === 'recording');
    const pipeline = issuesOnly.filter((r) => r.stage !== 'recording');
    return {
      pipelineIssues: sortIssues(pipeline),
      recordingIssues: sortIssues(recording),
      enrichmentRows: sortIssues(enrichments),
    };
  }, [clinicalEncounterId, issuesByEncounter]);

  // Backwards-compat: many spots still reference issueRows. Treat the
  // pipeline pile as the legacy "issueRows" since recording detectors
  // are not online yet (Phase 4 territory) — the recording zone will
  // be empty in v1 but the scaffolding is in place.
  const issueRows = pipelineIssues;

  const [showAllIssues, setShowAllIssues] = useState(false);
  const [showAllRecording, setShowAllRecording] = useState(false);
  const [showAllEnrichments, setShowAllEnrichments] = useState(false);
  const TOP_N = 5;
  const visibleIssues = showAllIssues ? issueRows : issueRows.slice(0, TOP_N);
  const visibleRecording = showAllRecording
    ? recordingIssues
    : recordingIssues.slice(0, TOP_N);
  const visibleEnrichments = showAllEnrichments
    ? enrichmentRows
    : enrichmentRows.slice(0, TOP_N);

  // Map issue → section. The pipeline persists `signal_ids` on each issue
  // and `target_sections` on each signal, but the FE only sees the issue
  // row. As a pragmatic surface, we look up section names mentioned in
  // the issue's evidence (which is what the rail card already shows). A
  // future iteration can plumb signal-level target_sections through the
  // /issues endpoint.
  const issuesBySection = useMemo(() => {
    const map: Record<string, IssueRow[]> = {};
    for (const iss of issueRows) {
      for (const span of iss.evidenceBundle ?? []) {
        if (span.source === 'summary' && span.section) {
          const key = String(span.section);
          if (!map[key]) map[key] = [];
          if (!map[key].includes(iss)) map[key].push(iss);
        }
      }
    }
    return map;
  }, [issueRows]);

  // When the auditor clicks "N issues" on a section card, jump to the first
  // transcript-side evidence span across that section's issues. Shows the
  // raw words behind the flag without forcing the auditor to expand the
  // issue card first. Falls back to scrolling to the section card itself
  // if there's no transcript evidence (e.g. a wrong_section flag whose
  // only evidence is in the summary).
  const handleSectionIssuesClick = (sectionKey: string) => {
    const list = issuesBySection[sectionKey] ?? [];
    for (const iss of list) {
      for (const span of iss.evidenceBundle ?? []) {
        if (
          span.source === 'transcript' &&
          typeof span.segmentOrderStart === 'number'
        ) {
          focusEvidence(span);
          return;
        }
      }
    }
    // No transcript evidence — re-anchor on the section itself.
    focusSection('summary', sectionKey);
  };

  const handleRunAudit = async () => {
    if (!clinicalEncounterId) return;
    setRunning(true);
    setRunError(null);
    try {
      await runPipeline(clinicalEncounterId);
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : 'Failed to run audit pipeline';
      setRunError(msg);
    } finally {
      setRunning(false);
    }
  };

  // Deep-link: `?focus=issue-<id>` auto-scrolls to that issue's first
  // evidence span once data + issues have loaded. Lets reviewers share a
  // URL pointing directly at a specific finding ("look at this case →").
  // We only fire once per id to avoid yanking the page on every re-render.
  const focusParam = searchParams.get('focus');
  const [autoFocusedFor, setAutoFocusedFor] = useState<string | null>(null);
  useEffect(() => {
    if (!focusParam) return;
    if (dataLoading || issuesLoading) return;
    if (autoFocusedFor === focusParam) return;
    if (!focusParam.startsWith('issue-')) return;
    const issueId = focusParam.slice('issue-'.length);
    const target =
      issueRows.find((i) => i.id === issueId) ??
      enrichmentRows.find((i) => i.id === issueId);
    if (!target) return;
    const span = target.evidenceBundle?.[0];
    if (span) focusEvidence(span);
    setAutoFocusedFor(focusParam);
  }, [
    focusParam,
    dataLoading,
    issuesLoading,
    issueRows,
    enrichmentRows,
    autoFocusedFor,
    focusEvidence,
  ]);

  // Initials for the header avatar.
  const initials = useMemo(() => {
    const parts = patientName.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    const first = parts[0][0] ?? '';
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return (first + last).toUpperCase() || '?';
  }, [patientName]);

  if (!clinicalEncounterId) {
    return (
      <div className="p-6 text-sm text-red-600">
        Missing encounter id in route.
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1700px] mx-auto">
      {/* Header */}
      <div className="mb-4">
        <Link
          to="/management/coverage-audit"
          className="text-xs text-purple-600 hover:underline"
        >
          ← Back to Coverage Audit
        </Link>
        <div className="mt-2 flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center font-semibold text-lg shrink-0">
              {initials}
            </div>
            <div>
              <h1 className="text-xl font-semibold leading-tight">
                {dataLoading ? 'Loading…' : patientName || '—'}
              </h1>
              <div className="text-sm text-gray-500 mt-0.5">
                <MetaItem>{encounterDate || '—'}</MetaItem>
                {encounterType && <MetaItem>{encounterType}</MetaItem>}
                {transcriptSegments.length > 0 && (
                  <MetaItem>{transcriptSegments.length} segments</MetaItem>
                )}
                {transcriptDuration > 0 && (
                  <MetaItem>{formatTimestamp(transcriptDuration)}</MetaItem>
                )}
                {transcriptWordCount > 0 && (
                  <MetaItem>{transcriptWordCount} words</MetaItem>
                )}
                {encounterMode && (
                  <span
                    className={`ml-1 inline-flex items-center text-[10px] px-1.5 py-0.5 rounded border font-medium uppercase tracking-wide ${
                      encounterMode === 'connected'
                        ? 'bg-blue-50 text-blue-700 border-blue-200'
                        : 'bg-purple-50 text-purple-700 border-purple-200'
                    }`}
                  >
                    {encounterMode === 'connected' ? 'EHR' : 'Standalone'}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-col items-end">
            <button
              type="button"
              onClick={handleRunAudit}
              disabled={running}
              className="px-4 py-2 rounded bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 disabled:opacity-50"
              title="Re-runs Azure Text Analytics for Health + LLM judge against this encounter."
            >
              {running
                ? 'Spot-checking…'
                : issueRows.length + enrichmentRows.length > 0
                  ? 'Re-run audit'
                  : 'Run audit'}
            </button>
            {runError && (
              <span className="text-xs text-red-600 mt-1">{runError}</span>
            )}
            {running && (
              <span className="text-[11px] text-gray-500 mt-1">
                Running TAH + LLM judge…
              </span>
            )}
          </div>
        </div>
      </div>

      {dataError && (
        <div className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
          {dataError}
        </div>
      )}

      {lastRunDebug && (
        <RunDebugPanel debug={lastRunDebug} />
      )}

      <div className="grid grid-cols-12 gap-4">
        {/* Transcript */}
        <Panel title="Transcript" className="col-span-12 lg:col-span-3">
          {dataLoading ? (
            <SkeletonLines lines={6} />
          ) : transcriptSegments.length === 0 ? (
            <Empty>No transcript content.</Empty>
          ) : (
            <div className="space-y-1.5">
              {transcriptSegments.map((seg) => {
                const style =
                  SPEAKER_STYLES[seg.speakerRole ?? 'other'] ??
                  SPEAKER_STYLES.other;
                return (
                  <div
                    key={seg.order}
                    id={`transcript-segment-${seg.order}`}
                    className={`rounded px-2 py-1.5 ring-1 ring-inset transition-shadow ${style.bg} ${style.ring}`}
                  >
                    <div className="flex items-center gap-2 text-[11px] text-gray-600 mb-0.5">
                      <span
                        className={`inline-block w-1.5 h-1.5 rounded-full ${style.dot}`}
                      />
                      <span className="font-semibold uppercase tracking-wide">
                        {seg.speakerName ?? style.label}
                      </span>
                      <span className="font-mono text-gray-500">
                        {formatTimestamp(seg.startSeconds)}
                      </span>
                    </div>
                    <div className="text-sm text-gray-800 whitespace-pre-wrap break-words">
                      {seg.text || (
                        <span className="italic text-gray-400">(empty)</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Panel>

        {/* Summary */}
        <Panel title="Summary" className="col-span-12 lg:col-span-3">
          {dataLoading ? (
            <SkeletonLines lines={6} />
          ) : !summary ? (
            <Empty>No summary available.</Empty>
          ) : (
            <SectionList
              keys={CLINICAL_SECTION_ORDER}
              labels={CLINICAL_SECTION_LABELS}
              source={summary as unknown as Record<string, ClinicalSection>}
              side="summary"
              issuesBySection={issuesBySection}
              onSectionIssuesClick={handleSectionIssuesClick}
            />
          )}
        </Panel>

        {/* Note / SOAP */}
        <Panel
          title="Note"
          className="col-span-12 lg:col-span-3"
          right={
            <div className="inline-flex rounded border bg-white text-[11px] overflow-hidden">
              <button
                type="button"
                onClick={() => setNoteView('sections')}
                className={`px-2 py-1 ${
                  noteView === 'sections'
                    ? 'bg-purple-600 text-white'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                Sections
              </button>
              <button
                type="button"
                onClick={() => setNoteView('soap')}
                className={`px-2 py-1 ${
                  noteView === 'soap'
                    ? 'bg-purple-600 text-white'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                SOAP
              </button>
            </div>
          }
        >
          {dataLoading ? (
            <SkeletonLines lines={6} />
          ) : !note && !noteRawString ? (
            <Empty>No medical note generated yet.</Empty>
          ) : note && noteView === 'sections' ? (
            <SectionList
              keys={MEDICAL_NOTE_SECTION_ORDER}
              labels={MEDICAL_NOTE_SECTION_LABELS}
              source={note as unknown as Record<string, MedicalNoteSection>}
              peer={summary as unknown as Record<string, ClinicalSection> | undefined}
              side="note"
            />
          ) : note && noteView === 'soap' ? (
            <SoapView
              source={note as unknown as Record<string, MedicalNoteSection>}
              onSectionClick={(key) => {
                // SOAP view doesn't render the per-section anchors. Switch
                // back to Sections so the target id exists, then scroll.
                // The double rAF gives React time to mount the new tree
                // before we ask the browser to find #note-section-<key>.
                setNoteView('sections');
                requestAnimationFrame(() =>
                  requestAnimationFrame(() => focusSection('note', key)),
                );
              }}
            />
          ) : noteFormat === 'html' ? (
            <div
              className="text-sm prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: noteRawString }}
            />
          ) : (
            <pre className="whitespace-pre-wrap break-words text-sm text-gray-800 font-sans">
              {noteRawString}
            </pre>
          )}
        </Panel>

        {/* Issues rail */}
        <aside className="col-span-12 lg:col-span-3 max-h-[80vh] overflow-y-auto space-y-4">
          {issuesError && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
              {issuesError}
            </div>
          )}

          {(issuesLoading || running) &&
          issueRows.length === 0 &&
          enrichmentRows.length === 0 ? (
            <div className="text-sm text-gray-500 italic border rounded p-3 bg-purple-50/40 border-purple-100">
              {running
                ? 'Spot-checking with TAH + LLM judge…'
                : 'Loading…'}
            </div>
          ) : issueRows.length === 0 && enrichmentRows.length === 0 ? (
            <div className="text-sm text-gray-500 italic border rounded p-3 bg-gray-50">
              No issues detected by the audit pipeline. Click{' '}
              <span className="font-semibold">Re-run audit</span> to re-check.
            </div>
          ) : (
            <>
              {/* Zone A — Pipeline issues. Things our AI did to the
                  conversion (transcript→summary, summary→note). These
                  are mostly auto-fixable; this is where the audit moves
                  the needle on system quality. */}
              <section>
                <header className="mb-2">
                  <h2 className="font-semibold text-sm uppercase tracking-wide text-purple-700">
                    Pipeline issues{' '}
                    <span className="text-xs font-normal text-gray-500">
                      ({issueRows.length})
                    </span>
                  </h2>
                  <p className="text-[11px] text-gray-500 leading-snug">
                    What our AI did to the conversion. Auto-fixable when
                    the detector found a deterministic pattern.
                  </p>
                </header>
                {issueRows.length === 0 ? (
                  <div className="text-sm text-gray-500 italic border rounded p-3 bg-gray-50">
                    No pipeline issues detected.
                  </div>
                ) : (
                  <>
                    {visibleIssues.map((iss) => (
                      <IssueCard
                        key={iss.id}
                        issue={iss}
                        onEvidenceClick={focusEvidence}
                        summary={summary}
                        note={note}
                      />
                    ))}
                    {issueRows.length > TOP_N && (
                      <button
                        type="button"
                        onClick={() => setShowAllIssues((v) => !v)}
                        className="mt-1 text-xs text-purple-600 hover:underline"
                      >
                        {showAllIssues
                          ? 'Show top 5 only'
                          : `Show all (${issueRows.length - TOP_N} more)`}
                      </button>
                    )}
                  </>
                )}
              </section>

              {/* Zone B — Recording / discovery issues. Things the
                  doctor missed during the visit. Manual only — these
                  are clinician-training feedback, not auto-fixes. v1
                  scaffolding: empty until the standard-of-care detector
                  ships in Phase 4. */}
              <section>
                <header className="mb-2">
                  <h2 className="font-semibold text-sm uppercase tracking-wide text-blue-700">
                    Recording issues{' '}
                    <span className="text-xs font-normal text-gray-500">
                      ({recordingIssues.length})
                    </span>
                  </h2>
                  <p className="text-[11px] text-gray-500 leading-snug">
                    Gaps in what the doctor captured during the visit.
                    Manual review — clinician-facing improvements.
                  </p>
                </header>
                {recordingIssues.length === 0 ? (
                  <div className="text-sm text-gray-500 italic border rounded p-3 bg-blue-50/30 border-blue-100">
                    No recording-quality issues detected. Standard-of-care
                    detection ships in Phase 4 — for now this zone may
                    appear empty even when the visit had gaps.
                  </div>
                ) : (
                  <>
                    {visibleRecording.map((iss) => (
                      <IssueCard
                        key={iss.id}
                        issue={iss}
                        onEvidenceClick={focusEvidence}
                        summary={summary}
                        note={note}
                      />
                    ))}
                    {recordingIssues.length > TOP_N && (
                      <button
                        type="button"
                        onClick={() => setShowAllRecording((v) => !v)}
                        className="mt-1 text-xs text-blue-600 hover:underline"
                      >
                        {showAllRecording
                          ? 'Show top 5 only'
                          : `Show all (${recordingIssues.length - TOP_N} more)`}
                      </button>
                    )}
                  </>
                )}
              </section>

              <section>
                <h2 className="font-semibold mb-2 text-sm uppercase tracking-wide text-gray-600">
                  Opportunities to enrich this note{' '}
                  <span className="text-xs font-normal text-gray-500">
                    ({enrichmentRows.length})
                  </span>
                </h2>
                {enrichmentRows.length === 0 ? (
                  <div className="text-sm text-gray-500 italic border rounded p-3 bg-gray-50">
                    No enrichment suggestions.
                  </div>
                ) : (
                  <>
                    {visibleEnrichments.map((iss) => (
                      <IssueCard
                        key={iss.id}
                        issue={iss}
                        onEvidenceClick={focusEvidence}
                        summary={summary}
                        note={note}
                      />
                    ))}
                    {enrichmentRows.length > TOP_N && (
                      <button
                        type="button"
                        onClick={() => setShowAllEnrichments((v) => !v)}
                        className="mt-1 text-xs text-purple-600 hover:underline"
                      >
                        {showAllEnrichments
                          ? 'Show top 5 only'
                          : `Show all (${enrichmentRows.length - TOP_N} more)`}
                      </button>
                    )}
                  </>
                )}
              </section>
            </>
          )}
        </aside>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Reusable building blocks
// ----------------------------------------------------------------------------

function MetaItem({ children }: { children: React.ReactNode }) {
  return (
    <span>
      {children}
      <span className="mx-1.5 text-gray-300">·</span>
    </span>
  );
}

function Panel({
  title,
  right,
  children,
  className,
}: {
  title: string;
  right?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section
      className={`bg-white border rounded shadow-sm flex flex-col max-h-[80vh] ${className ?? ''}`}
    >
      <header className="flex items-center justify-between px-3 py-2 border-b bg-gray-50/50 rounded-t">
        <h2 className="font-semibold text-xs uppercase tracking-wide text-gray-600">
          {title}
        </h2>
        {right}
      </header>
      <div className="p-3 overflow-y-auto">{children}</div>
    </section>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-sm text-gray-500 italic border border-dashed rounded p-3 text-center">
      {children}
    </div>
  );
}

function SkeletonLines({ lines }: { lines: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: lines }).map((_, i) => (
        <div
          key={i}
          className="h-3 bg-gray-100 rounded animate-pulse"
          style={{ width: `${65 + ((i * 13) % 35)}%` }}
        />
      ))}
    </div>
  );
}

interface SectionLike {
  title?: string;
  content?: string;
  status?: string;
  confidence?: number;
  // Physician enrichment. The summary stores a rich object with richText
  // (HTML from the editor) + plainText, while the medical note stores just
  // a plain string. Audit view treats either form as "this clinician
  // touched the section" and surfaces it under the AI content.
  physicianNotes?:
    | string
    | {
        richText?: string;
        plainText?: string;
        updatedAt?: string | Date;
        updatedBy?: string;
      };
  dictations?: Array<{
    transcription: string;
    audioMetadata?: { duration?: number; recordedAt?: string | Date };
    addedBy?: string;
  }>;
}

function getPhysicianNotePlainText(
  pn: SectionLike['physicianNotes'],
): string {
  if (!pn) return '';
  if (typeof pn === 'string') return pn.trim();
  if (typeof pn.plainText === 'string' && pn.plainText.trim().length > 0) {
    return pn.plainText.trim();
  }
  if (typeof pn.richText === 'string' && pn.richText.trim().length > 0) {
    return pn.richText.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  }
  return '';
}

// ----------------------------------------------------------------------------
// Discrepancy detection (Summary vs Note)
// ----------------------------------------------------------------------------

export type Discrepancy =
  | 'lost' // physician dictated on summary but note is empty
  | 'invented' // note has content but summary has none
  | 'modified' // both have content but text diverges
  | null;

// Conservative normalization for comparing whether two text blobs are
// "the same" for audit purposes. Collapses whitespace + strips trailing
// punctuation so identical content with different formatting doesn't fire
// a false-positive `modified` flag.
function normalizeForCompare(s: string | undefined): string {
  return (s ?? '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/[.,;:!?-]+/g, '')
    .trim();
}

function detectDiscrepancy(
  summarySec: SectionLike | undefined,
  noteSec: SectionLike | undefined,
): Discrepancy {
  const sumContent = (summarySec?.content ?? '').trim();
  const sumPhysician = getPhysicianNotePlainText(summarySec?.physicianNotes);
  const sumHasAny = sumContent.length > 0 || sumPhysician.length > 0;
  const noteContent = (noteSec?.content ?? '').trim();
  const noteHasAny = noteContent.length > 0;

  if (sumHasAny && !noteHasAny) return 'lost';
  if (!sumHasAny && noteHasAny) return 'invented';
  if (sumHasAny && noteHasAny) {
    // Compare the strongest summary signal (physician dictate beats AI
    // capture when both exist — that's what the clinician explicitly
    // authored).
    const summarySignal = sumPhysician.length > 0 ? sumPhysician : sumContent;
    if (normalizeForCompare(summarySignal) !== normalizeForCompare(noteContent)) {
      return 'modified';
    }
  }
  return null;
}

const DISCREPANCY_BADGE: Record<
  Exclude<Discrepancy, null>,
  { label: string; cls: string; tooltip: string }
> = {
  lost: {
    label: 'Lost',
    cls: 'bg-red-50 text-red-700 ring-1 ring-red-200',
    tooltip:
      'Summary has content (or physician dictation) but the note is empty for this section.',
  },
  invented: {
    label: 'Note-only',
    cls: 'bg-amber-50 text-amber-800 ring-1 ring-amber-200',
    tooltip:
      'Note has content but the summary never captured it — the note generator may have invented this.',
  },
  modified: {
    label: 'Modified',
    cls: 'bg-blue-50 text-blue-700 ring-1 ring-blue-200',
    tooltip:
      'Summary and note both have content but the wording diverges — note generator rewrote the section.',
  },
};

function SectionList<K extends string>({
  keys,
  labels,
  source,
  peer,
  side,
  issuesBySection,
  onSectionIssuesClick,
}: {
  keys: ReadonlyArray<K>;
  labels: Record<K, string>;
  source: Record<string, SectionLike>;
  peer?: Record<string, SectionLike>;
  side?: 'summary' | 'note';
  issuesBySection?: Record<string, IssueRow[]>;
  // Called when the user clicks the "N issues" badge on a section card.
  // Deep-links the auditor to the first relevant transcript snippet so
  // they see the raw evidence behind the flag without having to open the
  // issue card first.
  onSectionIssuesClick?: (key: K) => void;
}) {
  return (
    <div className="space-y-2">
      {keys.map((key) => {
        const sec = source[key];
        const peerSec = peer?.[key];
        // Only the Note side computes the discrepancy badge — it's the
        // side where the auditor decides whether the generated note
        // diverges from the source-of-truth (summary + physician notes).
        const discrepancy =
          side === 'note' ? detectDiscrepancy(peerSec, sec) : null;
        const sectionIssues = issuesBySection?.[key] ?? [];
        // Anchor id used by useEvidenceScroll: e.g. summary-section-treatment.
        // For peer-less lists (e.g. SOAP doesn't render via SectionList) this
        // hook is the single source of truth for which DOM node a click on
        // an evidence chip should land on.
        const anchorId = side ? `${side}-section-${key}` : undefined;
        return (
          <SectionCard
            key={key}
            id={anchorId}
            label={labels[key]}
            section={sec}
            discrepancy={discrepancy}
            issues={sectionIssues}
            onIssuesBadgeClick={
              onSectionIssuesClick
                ? () => onSectionIssuesClick(key)
                : undefined
            }
          />
        );
      })}
    </div>
  );
}

function SectionCard({
  id,
  label,
  section,
  discrepancy,
  issues,
  onIssuesBadgeClick,
}: {
  id?: string;
  label: string;
  section?: SectionLike;
  discrepancy?: Discrepancy;
  issues?: IssueRow[];
  onIssuesBadgeClick?: () => void;
}) {
  const status = section?.status ?? 'not_captured';
  const captured =
    status === 'captured' && (section?.content ?? '').trim().length > 0;
  const physicianText = getPhysicianNotePlainText(section?.physicianNotes);
  const dictations = (section?.dictations ?? []).filter(
    (d) => (d.transcription ?? '').trim().length > 0,
  );
  const hasEnrichment = physicianText.length > 0 || dictations.length > 0;

  // Highest severity issue targeting this section drives the card's
  // outline color so the auditor can see at a glance which sections the
  // pipeline flagged.
  const topSeverity =
    (issues ?? []).reduce<IssueSeverity | null>((acc, i) => {
      if (!acc) return i.severity;
      return SEVERITY_ORDER[i.severity] < SEVERITY_ORDER[acc] ? i.severity : acc;
    }, null);
  const issueRingCls = topSeverity
    ? topSeverity === 'major'
      ? 'ring-1 ring-red-300'
      : topSeverity === 'moderate'
        ? 'ring-1 ring-amber-300'
        : 'ring-1 ring-yellow-200'
    : '';

  return (
    <div
      id={id}
      className={`rounded border transition-shadow scroll-mt-24 ${
        captured || hasEnrichment
          ? 'border-gray-200'
          : 'border-dashed border-gray-200 bg-gray-50/30'
      } ${issueRingCls}`}
    >
      <div className="flex items-center justify-between px-2.5 py-1.5 border-b border-inherit gap-2">
        <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
          <span className="font-semibold text-[12px] text-gray-700">
            {label}
          </span>
          {hasEnrichment && (
            <span
              className="inline-flex items-center text-[9px] px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 font-semibold uppercase tracking-wide"
              title="Physician edited or dictated additional content"
            >
              MD
            </span>
          )}
          {discrepancy && (
            <span
              className={`inline-flex items-center text-[9px] px-1.5 py-0.5 rounded-full font-semibold uppercase tracking-wide ${DISCREPANCY_BADGE[discrepancy].cls}`}
              title={DISCREPANCY_BADGE[discrepancy].tooltip}
            >
              {DISCREPANCY_BADGE[discrepancy].label}
            </span>
          )}
          {(issues ?? []).length > 0 && (
            onIssuesBadgeClick ? (
              <button
                type="button"
                onClick={onIssuesBadgeClick}
                className="inline-flex items-center text-[9px] px-1.5 py-0.5 rounded-full bg-red-50 text-red-700 font-semibold uppercase tracking-wide hover:bg-red-100 cursor-pointer"
                title={`${issues!.length} audit issue${issues!.length > 1 ? 's' : ''} — click to jump to evidence`}
              >
                {issues!.length} issue{issues!.length > 1 ? 's' : ''} →
              </button>
            ) : (
              <span
                className="inline-flex items-center text-[9px] px-1.5 py-0.5 rounded-full bg-red-50 text-red-700 font-semibold uppercase tracking-wide"
                title={`${issues!.length} audit issue${issues!.length > 1 ? 's' : ''} flagged for this section`}
              >
                {issues!.length} issue{issues!.length > 1 ? 's' : ''}
              </span>
            )
          )}
        </div>
        <StatusPill captured={captured} confidence={section?.confidence} />
      </div>
      <div className="px-2.5 py-2 text-[13px] text-gray-800 whitespace-pre-wrap leading-relaxed">
        {captured ? (
          section!.content
        ) : (
          <span className="italic text-gray-400">Not captured</span>
        )}
      </div>

      {/* Physician enrichment — surfaces edits the clinician made on top of
          the AI capture. Critical for audit: it explains why the final note
          may diverge from the raw transcript. */}
      {physicianText.length > 0 && (
        <div className="px-2.5 pb-2">
          <div className="mt-1 border-l-2 border-indigo-300 pl-2 bg-indigo-50/40 rounded-r py-1.5">
            <div className="text-[10px] uppercase tracking-wide font-semibold text-indigo-700 mb-0.5">
              Physician notes
            </div>
            <div className="text-[12px] text-gray-800 whitespace-pre-wrap leading-relaxed">
              {physicianText}
            </div>
          </div>
        </div>
      )}

      {/* Dictations — separate audit trail of in-section dictations. */}
      {dictations.length > 0 && (
        <div className="px-2.5 pb-2 space-y-1">
          {dictations.map((d, i) => (
            <div
              key={i}
              className="border-l-2 border-amber-300 pl-2 bg-amber-50/40 rounded-r py-1.5"
            >
              <div className="text-[10px] uppercase tracking-wide font-semibold text-amber-700 mb-0.5">
                Dictation {dictations.length > 1 ? `· ${i + 1}` : ''}
              </div>
              <div className="text-[12px] text-gray-800 whitespace-pre-wrap leading-relaxed">
                {d.transcription}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusPill({
  captured,
  confidence,
}: {
  captured: boolean;
  confidence?: number;
}) {
  if (!captured) {
    return (
      <span className="inline-flex items-center text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">
        Empty
      </span>
    );
  }
  const pct = Math.round((confidence ?? 0) * 100);
  return (
    <span className="inline-flex items-center gap-1 text-[10px]">
      <span className="px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-medium">
        Captured
      </span>
      {pct > 0 && (
        <span className="text-gray-400 font-mono">{pct}%</span>
      )}
    </span>
  );
}

function SoapView({
  source,
  onSectionClick,
}: {
  source: Record<string, SectionLike>;
  // Optional: when present, the section key labels become buttons that
  // jump to that section's full card in the Note column. Lets the auditor
  // pivot from the SOAP overview into the section-level Note view without
  // losing context.
  onSectionClick?: (key: string) => void;
}) {
  return (
    <div className="space-y-3">
      {SOAP_GROUPS.map((group) => {
        // Include sections that either have AI content OR physician
        // enrichment. Either alone is auditable signal.
        const groupSections = group.keys
          .map((k) => {
            const sec = source[k];
            const hasContent =
              sec?.status === 'captured' &&
              (sec?.content ?? '').trim().length > 0;
            const physicianText = getPhysicianNotePlainText(sec?.physicianNotes);
            return { key: k, sec, hasContent, physicianText };
          })
          .filter(
            ({ hasContent, physicianText }) =>
              hasContent || physicianText.length > 0,
          );
        return (
          <div key={group.letter} className="border rounded">
            <div className="flex items-start gap-2 px-2.5 py-2 border-b bg-gray-50/40">
              <span
                className={`shrink-0 w-7 h-7 rounded text-white text-xs font-bold flex items-center justify-center ${SOAP_LETTER_STYLES[group.letter]}`}
              >
                {group.letter}
              </span>
              <div className="min-w-0">
                <div className="font-semibold text-[12px] text-gray-800">
                  {group.label}
                </div>
                <div className="text-[11px] text-gray-500 leading-snug">
                  {group.description}
                </div>
              </div>
            </div>
            <div className="p-2.5 space-y-3">
              {groupSections.length === 0 ? (
                <div className="text-[12px] italic text-gray-400">
                  Nothing captured for this group.
                </div>
              ) : (
                groupSections.map(({ key, sec, hasContent, physicianText }) => (
                  <div key={key}>
                    <div className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold mb-0.5 flex items-center gap-1.5">
                      {onSectionClick ? (
                        <button
                          type="button"
                          onClick={() => onSectionClick(String(key))}
                          className="hover:text-purple-700 hover:underline cursor-pointer"
                        >
                          {CLINICAL_SECTION_LABELS[key as keyof ClinicalSummary]}
                        </button>
                      ) : (
                        CLINICAL_SECTION_LABELS[key as keyof ClinicalSummary]
                      )}
                      {physicianText.length > 0 && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 font-semibold normal-case tracking-normal">
                          MD
                        </span>
                      )}
                    </div>
                    {hasContent && (
                      <div className="text-[13px] text-gray-800 whitespace-pre-wrap leading-relaxed">
                        {sec!.content}
                      </div>
                    )}
                    {physicianText.length > 0 && (
                      <div className="mt-1 border-l-2 border-indigo-300 pl-2 bg-indigo-50/40 rounded-r py-1">
                        <div className="text-[10px] uppercase tracking-wide font-semibold text-indigo-700 mb-0.5">
                          Physician notes
                        </div>
                        <div className="text-[12px] text-gray-800 whitespace-pre-wrap leading-relaxed">
                          {physicianText}
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Type aliases consumed in `as` casts above. Direct alias references
// satisfy the linter's no-unused-vars without a disable directive.
type _UnusedClinicalSection = ClinicalSection;
type _UnusedIsStructuredSummary = typeof isStructuredSummary;

// ----------------------------------------------------------------------------
// Run debug panel — shows the per-detector breakdown returned by /run.
// Collapsed by default to keep the page clean; expand to investigate.
// ----------------------------------------------------------------------------

function RunDebugPanel({ debug }: { debug: RunPipelineDebug }) {
  const [expanded, setExpanded] = useState(false);
  const headline = `${debug.output.totalSignals} signals → ${debug.output.issues} issues · ${debug.output.durationMs}ms`;
  return (
    <div className="mb-3 border border-gray-200 rounded bg-gray-50/40">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 text-xs text-gray-700 hover:bg-gray-100"
      >
        <span className="flex items-center gap-2">
          <span className="font-semibold uppercase tracking-wide text-gray-500">
            Last run
          </span>
          <span className="text-gray-700">{headline}</span>
          {debug.inputs.transcriptLooksBase64Recovered && (
            <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px]">
              transcript recovered
            </span>
          )}
        </span>
        <span className="text-gray-400">{expanded ? '▾' : '▸'}</span>
      </button>
      {expanded && (
        <div className="px-3 pb-3 grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
          <DebugBlock title="Inputs">
            <DebugRow label="Transcript chars" value={debug.inputs.transcriptLen} />
            <DebugRow label="Summary chars" value={debug.inputs.summaryLen} />
            <DebugRow label="Summary shape" value={debug.inputs.summaryShape} />
            <DebugRow
              label="Medical note"
              value={
                debug.inputs.medicalNoteLoaded
                  ? debug.inputs.medicalNoteFormat ?? 'loaded'
                  : 'not found'
              }
            />
          </DebugBlock>

          <DebugBlock title="TAH (entity graphs)">
            <DebugRow label="Transcript entities" value={debug.tah.transcriptEntities} />
            <DebugRow label="Transcript relations" value={debug.tah.transcriptRelations} />
            <DebugRow label="Summary entities" value={debug.tah.summaryEntities} />
            <DebugRow label="Summary relations" value={debug.tah.summaryRelations} />
          </DebugBlock>

          <DebugBlock title="Detectors (signals emitted)">
            <DebugRow label="TAH" value={debug.detectors.tahSignals} />
            <DebugRow label="LLM judge" value={debug.detectors.llmJudgeSignals} />
            <DebugRow
              label="LLM enrichments"
              value={debug.detectors.llmEnrichmentsSignals}
            />
            <DebugRow
              label="Standard of care"
              value={debug.detectors.standardOfCareSignals}
            />
            <DebugRow label="Note drift" value={debug.detectors.noteDriftSignals} />
            <DebugRow label="Note evidence-empty" value={debug.detectors.noteEvidenceEmptySignals} />
            <DebugRow label="Note section relocation" value={debug.detectors.noteSectionRelocationSignals} />
            <DebugRow
              label="Medication cross-check"
              value={
                debug.detectors.medicationCandidatesChecked > 0
                  ? `${debug.detectors.medicationSignals} / ${debug.detectors.medicationCandidatesChecked} checked`
                  : debug.detectors.medicationSignals
              }
            />
            <DebugRow
              label="RxNorm drug lookup"
              value={`${debug.detectors.medicationRxNormVerified} matched / ${debug.detectors.medicationRxNormNoMatch} no-match / ${debug.detectors.medicationRxNormErrors} err`}
            />
            <DebugRow
              label="IMO drug lookup"
              value={`${debug.detectors.medicationImoVerified} matched / ${debug.detectors.medicationImoNoMatch} no-match / ${debug.detectors.medicationImoErrors} err`}
            />
            <DebugRow label="Numeric fabrication" value={debug.detectors.numericSignals} />
          </DebugBlock>

          <DebugBlock title="Output">
            <DebugRow label="Total signals" value={debug.output.totalSignals} />
            <DebugRow label="Persisted signals" value={debug.output.signalsPersisted} />
            <DebugRow label="Issues" value={debug.output.issues} />
            <DebugRow label="Duration (ms)" value={debug.output.durationMs} />
            {Object.keys(debug.output.byFamily).length > 0 && (
              <div className="mt-1 pt-1 border-t border-gray-200">
                <div className="text-gray-500 mb-0.5">By family:</div>
                {Object.entries(debug.output.byFamily).map(([fam, n]) => (
                  <DebugRow key={fam} label={fam} value={n} />
                ))}
              </div>
            )}
          </DebugBlock>
        </div>
      )}
    </div>
  );
}

function DebugBlock({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="font-semibold text-[10px] uppercase tracking-wide text-gray-500 mb-1">
        {title}
      </div>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

function DebugRow({
  label,
  value,
}: {
  label: string;
  value: string | number;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-gray-600 truncate">{label}</span>
      <span className="font-mono text-gray-900 tabular-nums">{value}</span>
    </div>
  );
}
